import React from 'react';
import { useQuery } from 'react-query';
import { dispositivosService } from '../services/api';
import { 
  FiSmartphone, 
  FiWifi, 
  FiWifiOff
} from 'react-icons/fi';
import LoadingSpinner from '../components/LoadingSpinner';

const Dashboard = () => {
  // Llamar a todos los endpoints individuales en paralelo para diagnóstico
  const { data: estadisticasTotal, isLoading: loadingTotal } = useQuery(
    'estadisticas-total',
    async () => {
      const response = await dispositivosService.estadisticasTotal();
      return response.data;
    },
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  );
  
  const { data: estadisticasConectados, isLoading: loadingConectados } = useQuery(
    'estadisticas-conectados',
    async () => {
      const response = await dispositivosService.estadisticasConectados();
      return response.data;
    },
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  );
  
  const { data: estadisticasActivos, isLoading: loadingActivos } = useQuery(
    'estadisticas-activos',
    async () => {
      const response = await dispositivosService.estadisticasActivos();
      return response.data;
    },
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  );
  
  // Consulta de comandos pendientes removida por lentitud
  // const { data: estadisticasComandos, isLoading: loadingComandos } = useQuery(...)
  
  const { data: estadisticasBloqueados, isLoading: loadingBloqueados } = useQuery(
    'estadisticas-bloqueados',
    async () => {
      const response = await dispositivosService.estadisticasBloqueados();
      return response.data;
    },
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  );

  const isLoading = loadingTotal || loadingConectados || loadingActivos || loadingBloqueados;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Construir objeto de estadísticas desde las respuestas individuales
  const stats = {
    total_dispositivos: estadisticasTotal?.total || 0,
    dispositivos_conectados: estadisticasConectados?.conectados || 0,
    dispositivos_bloqueados: estadisticasBloqueados?.bloqueados || 0,
    comandos_pendientes: 0 // Removido: consulta muy lenta
  };

  // Calcular grupos
  const total = stats.total_dispositivos;
  const recientesCount = stats.dispositivos_conectados;
  const activosCount = estadisticasActivos?.activos || 0;
  const inactivosCount = total - recientesCount - activosCount;

  const grupos = [
    { grupo: 'Recientes', cantidad: recientesCount },
    { grupo: 'Activos', cantidad: activosCount },
    { grupo: 'Inactivos', cantidad: Math.max(0, inactivosCount) }
  ].filter(g => g.cantidad > 0).sort((a, b) => b.cantidad - a.cantidad);

  const tarjetasEstadisticas = [
    {
      titulo: 'Total Dispositivos',
      valor: stats.total_dispositivos || 0,
      icono: FiSmartphone,
      color: 'primary',
      descripcion: 'Dispositivos registrados'
    },
    {
      titulo: 'Dispositivos Conectados',
      valor: stats.dispositivos_conectados || 0,
      icono: FiWifi,
      color: 'green',
      descripcion: 'En línea actualmente'
    },
    {
      titulo: 'Dispositivos Bloqueados',
      valor: stats.dispositivos_bloqueados || 0,
      icono: FiWifiOff,
      color: 'red',
      descripcion: 'Dispositivos bloqueados'
    }
    // Tarjeta de Comandos Pendientes removida por lentitud en la consulta
  ];

  const obtenerColorClases = (color) => {
    const colores = {
      primary: 'bg-primary-500 text-white',
      green: 'bg-green-500 text-white',
      red: 'bg-red-500 text-white',
      yellow: 'bg-yellow-500 text-white',
      orange: 'bg-orange-500 text-white',
      blue: 'bg-blue-500 text-white'
    };
    return colores[color] || colores.primary;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Control de dispositivos móviles - Versión Simplificada</p>
      </div>

      {/* Tarjetas de estadísticas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {tarjetasEstadisticas.map((tarjeta, index) => {
          const Icon = tarjeta.icono;
          return (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600">{tarjeta.titulo}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{tarjeta.valor}</p>
                  <p className="text-xs text-gray-500 mt-1">{tarjeta.descripcion}</p>
                </div>
                <div className={`p-3 rounded-lg ${obtenerColorClases(tarjeta.color)} ml-4`}>
                  <Icon size={20} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Gráfico de grupos */}
      {grupos.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Dispositivos por Grupo</h3>
          <div className="space-y-4">
            {grupos.map((grupo, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-700">{grupo.grupo}</span>
                <div className="flex items-center">
                  <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                    <div 
                      className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${(grupo.cantidad / stats.total_dispositivos) * 100}%` 
                      }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-600 w-8 text-right">{grupo.cantidad}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Información del sistema */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Estado del Sistema</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium text-gray-700">Servidor MDM</span>
              <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Operativo</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium text-gray-700">Base de Datos</span>
              <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Conectada</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium text-gray-700">API REST</span>
              <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Activa</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Funciones Principales</h3>
          <div className="space-y-3">
            <button className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-primary-300 transition-colors duration-200">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-primary-500 rounded-full mr-3"></div>
                <span className="text-sm font-medium text-gray-700">Bloquear/Desbloquear Dispositivos</span>
              </div>
            </button>
            <button className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-primary-300 transition-colors duration-200">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-primary-500 rounded-full mr-3"></div>
                <span className="text-sm font-medium text-gray-700">Obtener Ubicación</span>
              </div>
            </button>
            <button className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-primary-300 transition-colors duration-200">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-primary-500 rounded-full mr-3"></div>
                <span className="text-sm font-medium text-gray-700">Gestionar SIMs</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 